// App do agenciador (0.21.0). Ele entra pelo link pessoal que o titular (ou o
// escritório) manda pelo WhatsApp, como o motorista, e vê só o que é dele: as
// cargas que agenciou (em andamento e as entregues), o que tem a receber (o
// agenciamento de cada carga, os acertos e o que os clientes ainda devem das
// cargas dele), os caminhões vazios da empresa, o Raio-X do frete e o Radar
// de retorno. Não lança nada: quem lança e corrige é o escritório.
import { acertos, caminhoesVazios, clientes, dadosDaPrevia, entregas, fretes, veiculos, viagens } from '../dados.js';
import { cargasDoAgenciador, situacaoDaCarga, situacaoDoAgenciamento } from '../lib/agenciador.js';
import { h, trocar } from '../lib/dom.js';
import { lugar, paradasDaCarga, textoDaRota, textoDaSituacao, textoDoMomento } from '../lib/entregas.js';
import { mensagemDeErro } from '../lib/erros.js';
import { data, dinheiro, numeroBR, plural } from '../lib/formato.js';
import { comContas } from '../lib/fretes.js';
import { saldoDoMotorista } from '../lib/motorista.js';
import { itensAReceber, separarAReceber } from '../lib/receber.js';
import { ir } from '../rotas.js';
import { hojeDaEmpresa, sair, sessao } from '../sessao.js';
import { casca } from '../ui/casca.js';
import { carregando, cartaoLink, confirmar, emblema, faixa, secao, vazio } from '../ui/componentes.js';
import { dicaDeInstalar, linhaDeValor, quitacaoDoAcerto, textoDoSaldo } from './motorista.js';
import { textoCaminhao } from './viagens.js';
import { linkDoTrajeto } from './raiox.js';

const nomeDaEmpresa = () => sessao.empresa?.nome_fantasia || sessao.empresa?.nome || 'a empresa';

// Tudo o que as telas usam, de uma vez (do aparelho; funciona sem internet).
async function dadosDoAgenciador() {
  const id = sessao.colaborador;
  const [cargas, listaViagens, frota, listaClientes, paradas, previa, dele] = await Promise.all([
    fretes.listar(), viagens.listar(), veiculos.listar(), clientes.listar(), entregas.listar(), dadosDaPrevia(id), acertos.doColaborador(id),
  ]);
  const hoje = hojeDaEmpresa();
  const saldo = saldoDoMotorista({ colaboradorId: id, ate: hoje, ...previa, acertos: dele });
  const caminhaoDa = (f) => {
    const v = listaViagens.find((x) => x.id === f.viagem_id);
    return v ? frota.find((x) => x.id === v.veiculo_id) ?? null : null;
  };
  return {
    hoje,
    cargas,
    clientes: listaClientes,
    paradas,
    saldo,
    acertos: dele,
    clienteDa: (f) => listaClientes.find((c) => c.id === f.cliente_id) ?? null,
    caminhaoDa,
    agenciamento: (f) => situacaoDoAgenciamento(f, { lancamentos: previa.lancamentos, acertos: dele }),
  };
}

function cartaoDaCarga(f, d) {
  const s = situacaoDaCarga(f, d.hoje);
  const ag = d.agenciamento(f);
  const caminhao = d.caminhaoDa(f);
  return cartaoLink({
    href: `#/a/cargas/${f.id}`,
    titulo: `${f.complemento_de ? 'Complemento · ' : ''}${dinheiro(f.valor_frete)} · ${data(f.data_carregamento)}`,
    linhas: [
      textoDaRota(f, paradasDaCarga(d.paradas, f.id)) || null,
      d.clienteDa(f)?.nome ?? null,
      caminhao ? textoCaminhao(caminhao) : null,
      ag.valor > 0 ? `Seu agenciamento: ${dinheiro(ag.valor)} · ${ag.texto}` : null,
    ],
    emblemas: [emblema(s.texto, s.tom)],
  });
}

// Os caminhões vazios da empresa (do servidor), com o radar de onde estão.
const linkDoRadar = (s) => `#/a/raiox?origem=${encodeURIComponent(s.lugar.cidade)}&uf=${encodeURIComponent(s.lugar.uf)}&caminhao=${s.veiculo.id}`;

function blocoDosVazios() {
  const lugarDosVazios = h('div', { class: 'lista' }, carregando('Buscando os caminhões vazios…'));
  const fuso = sessao.empresa?.fuso_horario;
  caminhoesVazios()
    .then((lista) => trocar(lugarDosVazios,
      lista.length === 0
        ? h('p', { class: 'pequeno' }, 'Nenhum caminhão vazio agora: todos estão carregados.')
        : lista.map((s) => h('div', { class: 'cartao' }, h('div', { class: 'cartao-corpo' },
          h('span', { class: 'cartao-titulo' }, textoCaminhao(s.veiculo)),
          h('span', { class: 'cartao-linha' }, textoDaSituacao(s, { fuso })),
          s.lugar?.cidade && s.lugar?.uf && h('a', { class: 'botao texto', href: linkDoRadar(s) }, `Ver o que sai de ${lugar(s.lugar.cidade, s.lugar.uf)}`),
          s.lugar?.cidade && s.lugar?.uf && h('a', { class: 'botao texto', href: linkDoTrajeto({ origem: s.lugar.cidade, uf: s.lugar.uf, caminhao: s.veiculo.id }) },
            'Planejar o trajeto'))))))
    .catch((erro) => trocar(lugarDosVazios, faixa(mensagemDeErro(erro), 'alerta', 'Os caminhões vazios não abriram agora')));
  return lugarDosVazios;
}

// ---------------------------------------------------------------------
// Início
// ---------------------------------------------------------------------
export function telaAgenciadorInicio({ busca }) {
  const corpo = h('div', { class: 'secao' }, carregando());
  (async () => {
    const d = await dadosDoAgenciador();
    const { andamento } = cargasDoAgenciador(d.cargas);
    const resumo = textoDoSaldo(d.saldo.aReceber);
    const devem = separarAReceber(itensAReceber(d.cargas, d.clientes), d.hoje).totais;
    trocar(corpo,
      cartaoLink({
        href: '#/a/receber',
        simbolo: 'dinheiro',
        titulo: resumo.titulo,
        linhas: [
          `Agenciamentos até hoje (${data(d.hoje)}), pela prévia do acerto`,
          devem.tudo > 0 && `Clientes das suas cargas devem ${dinheiro(devem.tudo)}${devem.atrasados > 0 ? ` (${dinheiro(devem.atrasados)} atrasado)` : ''}`,
        ],
        emblemas: [d.saldo.acertosNaoPagos.length > 0 && emblema('Acerto a pagar', 'alerta')],
      }),
      secao('Cargas em andamento', h('div', { class: 'lista' },
        andamento.length === 0
          ? h('p', { class: 'pequeno' }, 'Nenhuma carga sua em andamento. Quando o escritório lançar uma carga que você agenciou, ela aparece aqui.')
          : andamento.map((f) => cartaoDaCarga(f, d))), { contador: String(andamento.length) }),
      secao('Caminhões vazios', blocoDosVazios()),
      cartaoLink({ href: '#/a/raiox', simbolo: 'rota', titulo: 'Raio-X do frete', linhas: ['Antes de oferecer: piso da ANTT, custo, quanto pedir e o que tem de volta'] }),
      h('div', { class: 'acoes' },
        h('a', { class: 'botao secundario cheio', href: '#/a/cargas' }, 'Todas as minhas cargas'),
        h('a', { class: 'botao texto', href: '#/ajuda' }, 'Ajuda'),
        h('button', {
          class: 'botao texto',
          type: 'button',
          onClick: async () => {
            const ok = await confirmar({
              titulo: 'Sair deste celular?',
              texto: 'Para entrar de novo, você vai precisar de um link novo do dono da frota.',
              sim: 'Sair',
              perigo: true,
            });
            if (!ok) return;
            await sair();
            ir('/entrar', { substituir: true });
          },
        }, 'Sair deste celular')));
  })().catch((erro) => {
    console.error(erro);
    trocar(corpo, faixa(mensagemDeErro(erro), 'perigo'));
  });
  return casca({
    titulo: nomeDaEmpresa(),
    conteudo: [
      busca.get('bemvindo') && faixa(dicaDeInstalar() ?? 'Tudo certo: é só usar.', 'ok', 'Pronto! Este celular está ligado ao HSA Frota'),
      corpo,
    ],
  });
}

// ---------------------------------------------------------------------
// Cargas: em andamento e as entregues
// ---------------------------------------------------------------------
const ENTREGUES_NA_LISTA = 60;

export function telaAgenciadorCargas() {
  const corpo = h('div', { class: 'secao' }, carregando());
  (async () => {
    const d = await dadosDoAgenciador();
    const { andamento, entregues } = cargasDoAgenciador(d.cargas);
    trocar(corpo,
      andamento.length + entregues.length === 0 && vazio({
        simbolo: 'rota', titulo: 'Nenhuma carga ainda', texto: 'As cargas que você agenciou aparecem aqui quando o escritório lança.',
      }),
      andamento.length > 0 && secao('Em andamento', h('div', { class: 'lista' }, andamento.map((f) => cartaoDaCarga(f, d))),
        { contador: String(andamento.length) }),
      entregues.length > 0 && secao('Entregues', h('div', { class: 'lista' },
        entregues.slice(0, ENTREGUES_NA_LISTA).map((f) => cartaoDaCarga(f, d)),
        entregues.length > ENTREGUES_NA_LISTA && h('p', { class: 'pequeno' }, `Mostrando as ${ENTREGUES_NA_LISTA} mais recentes.`)),
      { contador: String(entregues.length) }));
  })().catch((erro) => {
    console.error(erro);
    trocar(corpo, faixa(mensagemDeErro(erro), 'perigo'));
  });
  return casca({ titulo: 'Minhas cargas', conteudo: corpo });
}

export function telaAgenciadorCarga({ params }) {
  const corpo = h('div', { class: 'secao' }, carregando());
  (async () => {
    const bruto = await fretes.obter(params.id);
    if (!bruto) {
      trocar(corpo, vazio({ simbolo: 'rota', titulo: 'Carga não encontrada', acao: h('a', { class: 'botao', href: '#/a/cargas' }, 'Ver as cargas') }));
      return;
    }
    const d = await dadosDoAgenciador();
    const f = comContas(bruto);
    const s = situacaoDaCarga(f, d.hoje);
    const ag = d.agenciamento(f);
    const caminhao = d.caminhaoDa(f);
    const paradas = paradasDaCarga(d.paradas, f.id);
    const fuso = sessao.empresa?.fuso_horario;
    const recebido = (valor, quando) => (Number(valor) > 0
      ? `${dinheiro(valor)} · ${quando ? `recebido em ${data(quando)}` : 'o cliente ainda não pagou'}`
      : 'sem');
    const linhas = [
      ['Valor do frete', dinheiro(f.valor_frete)],
      ['Adiantamento', recebido(f.adto_valor, f.adto_recebido_em)],
      ['Saldo do cliente', recebido(f.saldo_valor, f.saldo_recebido_em)],
      ['Seu agenciamento', ag.valor > 0 ? `${dinheiro(ag.valor)} · ${ag.texto}` : ag.texto],
      f.cte_numero && ['CT-e / Manifesto', f.cte_numero],
      (f.mercadoria || f.peso_kg) && ['Carga', [f.mercadoria, f.peso_kg ? `${numeroBR(f.peso_kg)} kg` : null].filter(Boolean).join(' · ')],
    ].filter(Boolean);
    trocar(corpo,
      h('div', { class: 'cartao destaque' }, h('div', { class: 'cartao-corpo' },
        h('span', { class: 'cartao-titulo' }, `${f.complemento_de ? 'Complemento · ' : ''}${dinheiro(f.valor_frete)} · carregada em ${data(f.data_carregamento)}`),
        textoDaRota(f, paradas) && h('span', { class: 'cartao-linha' }, textoDaRota(f, paradas)),
        d.clienteDa(f) && h('span', { class: 'cartao-linha' }, d.clienteDa(f).nome),
        caminhao && h('span', { class: 'cartao-linha' }, textoCaminhao(caminhao)),
        f.data_descarga && h('span', { class: 'cartao-linha' },
          `Entregue${f.destino_cidade ? ` em ${lugar(f.destino_cidade, f.destino_uf)}` : ''} ${f.entregue_em ? `em ${textoDoMomento(f.entregue_em, { fuso })}` : `no dia ${data(f.data_descarga)}`}.`),
        h('span', { class: 'cartao-emblemas' }, emblema(s.texto, s.tom)))),
      secao('Valores', h('dl', { class: 'linha-dados' }, linhas.flatMap(([t, v]) => [h('dt', null, t), h('dd', null, v)]))),
      paradas.length > 0 && secao('Entregas no caminho', h('ul', { class: 'lista-simples' }, paradas.map((p) => h('li', null,
        `${lugar(p.cidade, p.uf)}: ${p.data_entrega ? `entregue em ${data(p.data_entrega)}` : 'a entregar'}${p.observacao ? ` (${p.observacao})` : ''}`)))),
      h('p', { class: 'pequeno' }, 'Viu algo errado? Fale com o escritório: quem lança e corrige as cargas é a empresa.'));
  })().catch((erro) => {
    console.error(erro);
    trocar(corpo, faixa(mensagemDeErro(erro), 'perigo'));
  });
  return casca({ titulo: 'Carga', voltar: '/a/cargas', conteudo: corpo });
}

// ---------------------------------------------------------------------
// A receber: da empresa (agenciamentos, acertos) e o que os clientes devem
// ---------------------------------------------------------------------
export function telaAgenciadorReceber() {
  const corpo = h('div', { class: 'secao' }, carregando());
  (async () => {
    const d = await dadosDoAgenciador();
    const { saldo } = d;
    const resumo = textoDoSaldo(saldo.aReceber);
    const itens = itensAReceber(d.cargas, d.clientes);
    const grupos = separarAReceber(itens, d.hoje);
    const nomeDoCliente = (f) => d.clienteDa(f)?.nome ?? 'Cliente não informado';
    const cartaoDoCliente = (item) => {
      const f = item.frete;
      const atrasado = item.vencimento < d.hoje;
      const caminhao = d.caminhaoDa(f);
      return cartaoLink({
        href: `#/a/cargas/${f.id}`,
        titulo: `${dinheiro(item.valor)} · ${item.tipo === 'adiantamento' ? 'Adiantamento' : 'Saldo'} · ${nomeDoCliente(f)}`,
        linhas: [
          `${atrasado ? 'Venceu em' : 'Vence em'} ${data(item.vencimento)}`,
          [`Carga de ${data(f.data_carregamento)}`, caminhao ? textoCaminhao(caminhao) : null, textoDaRota(f) || null].filter(Boolean).join(' · '),
        ],
        emblemas: [atrasado ? emblema('Atrasado', 'perigo') : item.vencimento === d.hoje && emblema('Vence hoje', 'alerta')],
      });
    };
    trocar(corpo,
      h('div', { class: 'cartao destaque' }, h('div', { class: 'cartao-corpo' },
        h('span', { class: 'valor-grande' }, resumo.titulo),
        h('span', { class: 'cartao-linha' }, `Conta até hoje (${data(saldo.ate)}). É uma prévia: o valor certo sai no acerto, que o escritório confere.`))),
      saldo.acertosNaoPagos.length > 0 && secao('Acerto fechado, ainda não pago', h('div', { class: 'lista' },
        saldo.acertosNaoPagos.map((a) => linhaDeValor(`Acerto nº ${a.numero}/${a.ano} · ${data(a.data)}`, Math.abs(Number(a.saldo)), quitacaoDoAcerto(a))))),
      secao('Agenciamentos liberados', h('div', { class: 'lista' },
        saldo.ganhos.length === 0
          ? h('p', { class: 'pequeno' }, 'Nenhum desde o último acerto.')
          : saldo.ganhos.map((g) => {
            const [primeira, resto] = String(g.descricao ?? '').split(' · ');
            return linhaDeValor(primeira || 'Crédito', g.valor, resto || (g.data ? data(g.data) : null));
          })),
      { contador: String(saldo.ganhos.length) }),
      saldo.esperando.length > 0 && secao('Esperando o adiantamento do cliente', h('div', { class: 'lista' },
        h('p', { class: 'pequeno' }, 'O agenciamento fica liberado quando o cliente paga o adiantamento da carga.'),
        saldo.esperando.map((g) => linhaDeValor('Agenciamento', g.valor, [
          String(g.descricao ?? '').split(' · ')[1], g.frete && nomeDoCliente(g.frete), g.frete && `adiantamento de ${dinheiro(g.frete.adto_valor)}`,
        ].filter(Boolean).join(' · ')))),
      { contador: String(saldo.esperando.length) }),
      saldo.vales.length > 0 && secao('Vales que você recebeu', h('div', { class: 'lista' },
        saldo.vales.map((l) => linhaDeValor(`Vale de ${data(l.data)}`, l.valor, l.descricao)))),
      saldo.descontos.length > 0 && secao('Descontos', h('div', { class: 'lista' },
        saldo.descontos.map((l) => linhaDeValor(`${l.descricao || 'Desconto'} · ${data(l.data)}`, l.valor)))),
      secao('O que os clientes devem das suas cargas', h('div', { class: 'lista' },
        itens.length === 0
          ? h('p', { class: 'pequeno' }, 'Nada: os clientes das suas cargas estão em dia.')
          : [
            h('p', null, `Total: ${dinheiro(grupos.totais.tudo)}${grupos.totais.atrasados > 0 ? `, ${dinheiro(grupos.totais.atrasados)} atrasado` : ''}.`),
            itens.map(cartaoDoCliente),
          ]),
      { contador: plural(itens.length, 'item', 'itens') }),
      d.acertos.length > 0 && secao('Acertos', h('div', { class: 'lista' },
        d.acertos.slice(0, 12).map((a) => linhaDeValor(`Nº ${a.numero}/${a.ano} · ${data(a.data)}`, Math.abs(Number(a.saldo)), quitacaoDoAcerto(a))))));
  })().catch((erro) => {
    console.error(erro);
    trocar(corpo, faixa(mensagemDeErro(erro), 'perigo'));
  });
  return casca({ titulo: 'A receber', conteudo: corpo });
}
